// src/services/authService.js
import { 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword,
    signOut,
    updateProfile,
    sendPasswordResetEmail
  } from "firebase/auth";
  
  import { 
    doc, 
    setDoc, 
    getDoc, 
    updateDoc, 
    serverTimestamp 
  } from "firebase/firestore";
  
  // Add these imports for storage
  import { 
    ref, 
    uploadBytes, 
    getDownloadURL 
  } from "firebase/storage";
  import { auth, db, storage } from "../firebase/config";
  
  export const registerUser = async (userData) => {
    try {
      // Create auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        userData.email, 
        userData.password
      );
      
      // Add display name - use updateProfile instead of updateProfile
      await updateProfile(userCredential.user, {
        displayName: userData.name
      });
      
      // Create user document in Firestore
      const userDocRef = doc(db, "users", userCredential.user.uid);
      await setDoc(userDocRef, {
        name: userData.name,
        email: userData.email,
        title: userData.title || '',
        school: userData.school || '',
        district: userData.district || '',
        role: userData.role || 'teacher',
        profileImage: null,
        createdAt: new Date()
      });
      
      return userCredential.user;
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  };
  
  export const loginUser = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };
  
  export const logoutUser = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };
  
  export const getCurrentUser = () => {
    return auth.currentUser;
  };
  
  export const getUserProfile = async (userId) => {
    try {
      const userDocRef = doc(db, "userProfiles", userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      } else {
        throw new Error("User not found");
      }
    } catch (error) {
      console.error("Error getting user profile:", error);
      throw error;
    }
  };
  
  export const updateUserProfile = async (userId, profileData) => {
    try {
      const userDocRef = doc(db, "userProfiles", userId);
      await updateDoc(userDocRef, profileData);
      
      // If updating display name
      if (profileData.name && auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: profileData.name
        });
      }
      
      return await getUserProfile(userId);
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  };
  
  export const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  };
  
  // Upload profile image
  export const uploadProfileImage = async (userId, imageFile) => {
    try {
      // Create a storage reference
      const storageRef = ref(storage, `profile-images/${userId}_${Date.now()}`);
      
      // Upload the file
      await uploadBytes(storageRef, imageFile);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      return {
        url: downloadURL,
        path: storageRef.fullPath
      };
    } catch (error) {
      console.error("Error uploading profile image:", error);
      throw error;
    }
  };